import React, { useState, useEffect } from 'react';
import { ChevronDownIcon, ClockIcon, GitBranchIcon, GitCommitIcon } from 'lucide-react';
import VersionDiffModal from './VersionDiffModal';
import CommitVersionModal from './CommitVersionModal';
import { useAuth } from '../contexts/AuthContext';
import { useSheetData } from '../contexts/SheetDataContext';

/**
 * Version Menu Component - shows current version and allows access to version history
 */
const VersionMenu = ({ sheetId, currentVersion, onVersionSelect }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [versions, setVersions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [diffModal, setDiffModal] = useState({ 
    isOpen: false, 
    fromVersion: null, 
    toVersion: null 
  });
  const [commitModalOpen, setCommitModalOpen] = useState(false);
  const { isAuthenticated } = useAuth();
  const { checkoutVersion, checkedOutVersion } = useSheetData();

  // Fetch version history when menu opens
  const fetchVersionHistory = async () => {
    if (!sheetId || loading) return;
    
    setLoading(true);
    try {
      const response = await fetch(`/api/sheets/${sheetId}/versions`, {
        credentials: 'include'
      });
      
      if (response.ok) {
        const result = await response.json();
        setVersions(result.data.versions || []);
      } else {
        console.error('Failed to fetch version history');
      }
    } catch (error) {
      console.error('Error fetching version history:', error);
    } finally {
      setLoading(false);
    }
  };

  // Load versions when menu opens
  useEffect(() => {
    if (isOpen) {
      fetchVersionHistory();
    }
  }, [isOpen, sheetId]);

  const handleVersionClick = async (version) => {
    // Checkout the version to view it
    await checkoutVersion(version);
    
    if (onVersionSelect) {
      onVersionSelect(version);
    }
    setIsOpen(false);
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString() + ' ' + date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  const handleViewDiff = (version) => {
    // Compare this version with the current version
    const fromVersion = Math.min(version.version_number, currentVersion);
    const toVersion = Math.max(version.version_number, currentVersion);
    
    setDiffModal({
      isOpen: true,
      fromVersion,
      toVersion
    });
    setIsOpen(false);
  };

  const handleRevert = async (version) => {
    // Confirm the revert action
    const confirmed = window.confirm(
      `Are you sure you want to revert to version ${version.version_number}?\n\n` +
      `This will restore the sheet to: "${version.message}"\n\n` +
      `This action cannot be undone.`
    );
    
    if (!confirmed) return;
    
    try {
      const response = await fetch(`/api/sheets/${sheetId}/versions/${version._id}/revert`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        }
      });
      
      if (response.ok) {
        const result = await response.json();
        alert(`Successfully reverted to version ${version.version_number}`);
        
        // Reload the page to show the reverted content
        window.location.reload();
      } else {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to revert to version');
      }
    } catch (error) {
      console.error('Error reverting to version:', error);
      alert(`Error reverting to version: ${error.message}`);
    }
    
    setIsOpen(false);
  };

  const handleCommitVersion = async (commitMessage) => {
    try {
      console.log('Committing version with message:', commitMessage);
      
      // Make API call to commit version
      const response = await fetch(`/api/sheets/${sheetId}/versions`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          message: commitMessage
        }),
      });
      
      const result = await response.json();
      
      if (!response.ok) {
        throw new Error(result.error || 'Failed to commit version');
      }
      
      console.log('Version committed successfully:', result);
      
      // Show success notification
      alert(`Version committed: "${commitMessage}"`);
      
      // Refresh version history
      if (isOpen) {
        fetchVersionHistory();
      }
      
      // Refresh the page to show updated version
      window.location.reload();
      
    } catch (error) {
      console.error('Error committing version:', error);
      alert(`Error committing version: ${error.message}`);
      throw error; // Re-throw to handle in modal
    }
  };

  return (
    <div className="relative">
      {/* Version Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={`flex items-center space-x-1 px-3 py-1 rounded-md font-medium text-sm transition-colors duration-150 ${
          checkedOutVersion 
            ? 'bg-orange-100 hover:bg-orange-200 text-orange-800' 
            : 'bg-blue-100 hover:bg-blue-200 text-blue-800'
        }`}
        title={checkedOutVersion ? `Viewing version ${checkedOutVersion.version_number} (not current)` : "View version history"}
      >
        <GitBranchIcon className="w-4 h-4" />
        <span>v{checkedOutVersion ? checkedOutVersion.version_number : (currentVersion || 1)}</span>
        {checkedOutVersion && <span className="text-xs">(viewing)</span>}
        <ChevronDownIcon className="w-3 h-3" />
      </button>

      {/* Version Dropdown Menu */}
      {isOpen && (
        <>
          {/* Backdrop */}
          <div 
            className="fixed inset-0 z-40" 
            onClick={() => setIsOpen(false)}
          />
          
          {/* Menu */}
          <div className="absolute right-0 top-full mt-1 w-80 bg-white shadow-lg rounded-md border border-gray-200 py-2 z-50 max-h-80 overflow-y-auto">
            <div className="px-3 py-2 border-b border-gray-100">
              <div className="space-y-2">
                <h3 className="font-medium text-gray-900 flex items-center">
                  <ClockIcon className="w-4 h-4 mr-2" />
                  Version History
                  {checkedOutVersion && (
                    <span className="ml-2 text-xs bg-orange-100 text-orange-700 px-2 py-1 rounded">
                      Viewing v{checkedOutVersion.version_number}
                    </span>
                  )}
                </h3>
                <div className="flex items-center space-x-2">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      if (!isAuthenticated) {
                        alert('You must be logged in to commit versions. Please sign in and try again.');
                        return;
                      }
                      setCommitModalOpen(true);
                      setIsOpen(false);
                    }}
                    className="flex items-center space-x-1 bg-blue-600 hover:bg-blue-700 text-white px-2 py-1 rounded text-xs font-medium transition-colors"
                    title={checkedOutVersion ? "Commit this version as new (preserves history)" : "Commit new version"}
                  >
                    <GitCommitIcon className="w-3 h-3" />
                    <span>Commit</span>
                  </button>
                  {checkedOutVersion && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        // Show diff between checked out version and current version
                        const fromVersion = Math.min(checkedOutVersion.version_number, currentVersion);
                        const toVersion = Math.max(checkedOutVersion.version_number, currentVersion);
                        setDiffModal({
                          isOpen: true,
                          fromVersion,
                          toVersion
                        });
                        setIsOpen(false);
                      }}
                      className="flex items-center space-x-1 bg-gray-600 hover:bg-gray-700 text-white px-2 py-1 rounded text-xs font-medium transition-colors"
                      title="View differences with current version"
                    >
                      <span>Diff</span>
                    </button>
                  )}
                  {checkedOutVersion && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        checkoutVersion(null); // Return to current version
                        setIsOpen(false);
                      }}
                      className="flex items-center space-x-1 bg-gray-500 hover:bg-gray-600 text-white px-2 py-1 rounded text-xs font-medium transition-colors"
                      title="Return to current version (cancel viewing)"
                    >
                      <span>Cancel</span>
                    </button>
                  )}
                </div>
              </div>
            </div>
            
            {loading ? (
              <div className="px-3 py-4 text-center text-gray-500">
                Loading versions...
              </div>
            ) : versions.length === 0 ? (
              <div className="px-3 py-4 text-center text-gray-500">
                No versions found
              </div>
            ) : (
              <div className="max-h-64 overflow-y-auto">
                {versions.map((version) => (
                  <div
                    key={version._id}
                    className={`px-3 py-3 hover:bg-gray-50 cursor-pointer border-b border-gray-50 last:border-b-0 ${
                      checkedOutVersion && version._id === checkedOutVersion._id 
                        ? 'bg-orange-50 border-orange-200' 
                        : version.version_number === currentVersion 
                          ? 'bg-blue-50' 
                          : ''
                    }`}
                    onClick={() => handleVersionClick(version)}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center space-x-2">
                          <span className={`font-medium ${
                            checkedOutVersion && version._id === checkedOutVersion._id
                              ? 'text-orange-700'
                              : version.version_number === currentVersion 
                                ? 'text-blue-700' 
                                : 'text-gray-900'
                          }`}>
                            v{version.version_number}
                          </span>
                          {checkedOutVersion && version._id === checkedOutVersion._id && (
                            <span className="bg-orange-100 text-orange-700 text-xs px-2 py-0.5 rounded-full">
                              Viewing
                            </span>
                          )}
                          {version.version_number === currentVersion && !checkedOutVersion && (
                            <span className="bg-blue-100 text-blue-700 text-xs px-2 py-0.5 rounded-full">
                              Current
                            </span>
                          )}
                        </div>
                        <p className="text-sm text-gray-600 mt-1 line-clamp-2">
                          {version.message}
                        </p>
                        <div className="flex items-center text-xs text-gray-500 mt-2">
                          <span>{version.committed_by?.username || 'Unknown'}</span>
                          <span className="mx-2">•</span>
                          <span>{formatDate(version.committed_at)}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
            
            {/* Footer */}
            <div className="px-3 py-2 border-t border-gray-100 bg-gray-50">
              <p className="text-xs text-gray-500">
                Click a version to view details • Commit a previous version to restore it
              </p>
            </div>
          </div>
        </>
      )}
      
      {/* Version Diff Modal */}
      <VersionDiffModal
        isOpen={diffModal.isOpen}
        onClose={() => setDiffModal({ isOpen: false, fromVersion: null, toVersion: null })}
        sheetId={sheetId}
        fromVersion={diffModal.fromVersion}
        toVersion={diffModal.toVersion}
      />
      
      {/* Commit Version Modal */}
      <CommitVersionModal
        isOpen={commitModalOpen}
        onClose={() => setCommitModalOpen(false)}
        onCommit={handleCommitVersion}
        sheetTitle="Current Sheet"
      />
    </div>
  );
};

export default VersionMenu;